<template>
  <v-hover v-slot="{ hover }" v-if="ads._id">
    <v-card
      class="premium-ad-card mt-7 mb-6"
      :class="{ 'premium-ad-card--hovered': hover }"
      width="100%"
      elevation="0"
      v-observe-visibility="{
        callback: visibilityChanged,
        intersection: {
          threshold: 0.8
        },
        throttle: 600,
        throttleOptions: {
          leading: 'visible'
        }
      }"
    >
      <!-- Premium Header with Glassmorphism Effect -->
      <div class="premium-header">
        <div class="premium-header__content">
          <div class="premium-avatar-container">
            <editable-avatar
              :image="ads.business_image"
              :editable="false"
              :size="48"
              class="premium-avatr"
            />
            <div class="premium-avatar__glow"></div>
          </div>
          
          <div class="premium-business-info">
            <h3 class="premium-business-name">{{ ads.business_name }}</h3>
            <p class="premium-business-subtitle">Sponsored Content</p>
          </div>
          
          <div class="premium-badge">
            <v-icon size="12" class="premium-badge__icon" color="#FBA518">mdi-star</v-icon>
            <span class="premium-badge__text">Premium</span>
          </div>
        </div>
        
        <!-- Animated Background Gradient -->
        <div class="premium-header__bg"></div>
      </div>

      <!-- Media Content with Enhanced Interactions -->
      <div class="premium-media-container">
        <!-- Image Ad -->
        <div 
          v-if="ads.type == 'image'" 
          class="premium-image-wrapper"
          @click="goToWebsite"
        >
          <v-img
            :src="ads.file"
            alt="advertisement"
            cover
            class="premium-image"
            :class="{ 'premium-image--hovered': hover }"
          />
          
          <!-- Interactive Overlay -->
          <div class="premium-overlay" :class="{ 'premium-overlay--visible': hover }">
            <div class="premium-overlay__content">
              <v-btn
                fab
                large
                color="white"
                class="premium-cta-btn"
                elevation="8"
              >
                <v-icon color="primary" size="24">mdi-open-in-new</v-icon>
              </v-btn>
              <p class="premium-overlay__text">Click to explore</p>
            </div>
          </div>
          
          <!-- Shimmer Effect -->
          <div class="premium-shimmer" :class="{ 'premium-shimmer--active': hover }"></div>
        </div>

        <!-- Video Ad -->
        <div v-else-if="ads.type == 'video' && ads.file" class="premium-video-wrapper">
          <div class="premium-video-container">
            <vue-plyr
              ref="plyr"
              class="premium-video-player"
              :options="{
                autoplay: false,
                muted: true,
                autopause: true,
                controls: [
                  'play',
                  'progress',
                  'current-time',
                  'mute',
                  'volume',
                  'fullscreen'
                ]
              }"
            >
              <video
                controls
                crossorigin
                playsinline
                id="videoElement"
                @ended="onEnded"
                class="premium-video"
              >
                <source :src="ads.file" type="video/mp4" />
              </video>
            </vue-plyr>
            
            <!-- Floating Action Button -->
            <div class="premium-video-overlay" :class="{ 'premium-video-overlay--visible': hover }">
              <v-btn
                fab
                color="rgba(255,255,255,0.95)"
                class="premium-video-cta"
                elevation="12"
                @click="goToWebsite"
              >
                <v-icon color="primary" size="20">mdi-link-variant</v-icon>
              </v-btn>
            </div>
          </div>
          
          <!-- Video Progress Indicator -->
          <div class="premium-video-indicator">
            <div class="premium-video-indicator__bar"></div>
          </div>
        </div>
      </div>

      <!-- Premium Content Section -->
      <div class="premium-content" @click="goToWebsite">
        <div class="premium-content__inner">
          <p class="premium-text">{{ ads.text }}</p>
          
          <!-- Interactive Elements -->
          <div class="premium-interactions" :class="{ 'premium-interactions--visible': hover }">
            <div class="premium-engagement">
              <v-icon size="16" class="premium-engagement__icon">mdi-heart-outline</v-icon>
              <v-icon size="16" class="premium-engagement__icon">mdi-share-variant</v-icon>
              <v-icon size="16" class="premium-engagement__icon">mdi-bookmark-outline</v-icon>
            </div>
            
            <v-btn 
              small 
              rounded 
              color="primary" 
              class="premium-learn-more"
              elevation="2"
            >
              Learn More
              <v-icon right size="14">mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>
        
        <!-- Subtle Gradient Background -->
        <div class="premium-content__bg"></div>
      </div>
      
      <!-- Floating Elements for Premium Feel -->
      <div class="premium-floating-elements">
        <div class="premium-particle premium-particle--1"></div>
        <div class="premium-particle premium-particle--2"></div>
        <div class="premium-particle premium-particle--3"></div>
      </div>
    </v-card>
  </v-hover>
</template>

<script>
import { mapActions } from "vuex";
import EditableAvatar from "../EditableAvatar.vue";

export default {
  components: { EditableAvatar },
  props: {
    type: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      ads: {}
    };
  },
  methods: {
    ...mapActions("ads", {
      getAds: "getAds"
    }),
    goToWebsite() {
      const url = this.ads.url.startsWith("http")
        ? this.ads.url
        : `http://${this.ads.url}`;
      window.open(url);
    },
    onEnded() {
      this.$refs.plyr.player.play();
    },
    visibilityChanged(isVisible, entry) {
      if (isVisible && entry.intersectionRatio > 0.5) {
        if (this.ads.type == "video") {
          this.$refs.plyr.player.muted = true;
          this.$refs.plyr.player.play();
        }
      } else if (this.$refs.plyr) {
        this.$refs.plyr.player.muted = true;
      }
    }
  },
  mounted() {
    this.getAds({ type: this.type })
      .then(res => {
        this.ads = res;
      })
      .catch(error => {
        console.log(error.response.data.message);
      });
  }
};
</script>

<style scoped>
/* Premium Card Base */
.premium-ad-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.06),
    0 4px 16px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  cursor: pointer;
}

.premium-ad-card--hovered {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 
    0 20px 64px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

/* Premium Header */
.premium-header {
  position: relative;
  padding: 20px 24px;
  z-index: 2;
}

.premium-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 2;
}

.premium-header__bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(99, 102, 241, 0.05) 0%, 
    rgba(236, 72, 153, 0.05) 50%, 
    rgba(59, 130, 246, 0.05) 100%);
  border-radius: 24px 24px 0 0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.premium-ad-card--hovered .premium-header__bg {
  opacity: 1;
}

/* Avatar Styling */
.premium-avatar-container {
  position: relative;
}

.premium-avatar {
  border: 3px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.premium-avatar__glow {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg,#084468,#0a5b8a);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.premium-ad-card--hovered .premium-avatar__glow {
  opacity: 0.6;
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
}

/* Business Info */
.premium-business-info {
  flex: 1;
}

.premium-business-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  line-height: 1.2;
}

.premium-business-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0 0;
  font-weight: 400;
}

/* Premium Badge */
.premium-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg,#084468,#0a5b8a);
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.premium-badge__icon {
  animation: twinkle 2s ease-in-out infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

/* Media Container */
.premium-media-container {
  position: relative;
  margin: 0 24px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* Image Styling */
.premium-image-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  cursor: pointer;
}

.premium-image {
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  border-radius: 16px;
}

.premium-image--hovered {
  transform: scale(1.05);
}

/* Interactive Overlay */
.premium-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.3) 0%, 
    rgba(0, 0, 0, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  opacity: 0;
  transition: all 0.3s ease;
  border-radius: 16px;
}

.premium-overlay--visible {
  opacity: 1;
}

.premium-overlay__content {
  text-align: center;
  transform: translateY(20px);
  transition: transform 0.3s ease;
}

.premium-overlay--visible .premium-overlay__content {
  transform: translateY(0);
}

.premium-cta-btn {
  margin-bottom: 12px !important;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.premium-overlay__text {
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

/* Shimmer Effect */
.premium-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.6s ease;
}

.premium-shimmer--active {
  left: 100%;
}

/* Video Styling */
.premium-video-wrapper {
  position: relative;
}

.premium-video-container {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
}

.premium-video-overlay {
  position: absolute;
  top: 16px;
  right: 16px;
  opacity: 0;
  transition: all 0.3s ease;
  transform: scale(0.8);
}

.premium-video-overlay--visible {
  opacity: 1;
  transform: scale(1);
}

.premium-video-cta {
  backdrop-filter: blur(10px);
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 6px 24px rgba(99, 102, 241, 0.5); }
}

/* Content Section */
.premium-content {
  position: relative;
  padding: 24px;
  cursor: pointer;
}

.premium-content__inner {
  position: relative;
  z-index: 2;
}

.premium-text {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  margin: 0 0 16px 0;
  font-weight: 400;
}

.premium-interactions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.premium-interactions--visible {
  opacity: 1;
  transform: translateY(0);
}

.premium-engagement {
  display: flex;
  gap: 16px;
}

.premium-engagement__icon {
  color: #64748b;
  transition: all 0.2s ease;
  cursor: pointer;
}

.premium-engagement__icon:hover {
  color: #3b82f6;
  transform: scale(1.1);
}

.premium-learn-more {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.25px;
}

.premium-content__bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(59, 130, 246, 0.02) 0%, 
    rgba(147, 51, 234, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.premium-ad-card--hovered .premium-content__bg {
  opacity: 1;
}

/* Floating Elements */
.premium-floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.premium-particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  border-radius: 50%;
  opacity: 0;
}

.premium-particle--1 {
  top: 20%;
  left: 10%;
  animation: float-particle 4s ease-in-out infinite;
}

.premium-particle--2 {
  top: 60%;
  right: 15%;
  animation: float-particle 3s ease-in-out infinite 1s;
}

.premium-particle--3 {
  bottom: 30%;
  left: 80%;
  animation: float-particle 5s ease-in-out infinite 2s;
}

.premium-ad-card--hovered .premium-particle {
  opacity: 0.6;
}

@keyframes float-particle {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
    opacity: 0.6; 
  }
  50% { 
    transform: translateY(-20px) rotate(180deg); 
    opacity: 0.9; 
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .premium-header__content {
    gap: 12px;
  }
  
  .premium-media-container,
  .premium-content {
    margin: 0 16px;
    padding: 16px;
  }
  
  .premium-business-name {
    font-size: 14px;
  }
  
  .premium-text {
    font-size: 13px;
  }
}

/* Dark Mode Support */
/* @media (prefers-color-scheme: dark) {
  .premium-ad-card {
    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .premium-business-name {
    color: #f1f5f9;
  }
  
  .premium-text {
    color: #cbd5e1;
  }
} */
</style>